import { Component } from '@theme/component';

/** 独立专题页交互；事件限定在当前实例，支持编辑器重渲染。 */
class SolutionsPage extends Component {
  /** @type {AbortController | undefined} */
  #listeners;
  /** @type {ResizeObserver | undefined} */
  #navigationSize;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanup();
  }

  #cleanup() {
    this.#listeners?.abort();
    this.#navigationSize?.disconnect();
  }

  #initialize() {
    this.#cleanup();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    this.addEventListener('click', this.#click, { signal });
    this.addEventListener('keydown', this.#keyDown, { signal });
    this.addEventListener('focusin', this.#focusIn, { signal });
    this.addEventListener('pointerover', this.#pointerOver, { signal });
    this.addEventListener('shopify:block:select', this.#selectBlock, { signal });
    this.classList.add('is-enhanced');
    const nav = this.querySelector('.nav');
    if (nav instanceof HTMLElement) {
      this.#navigationSize = new ResizeObserver(() => {
        this.style.setProperty('--solutions-nav-height', `${nav.offsetHeight}px`);
      });
      this.#navigationSize.observe(nav);
    }
    this.#setMenu(false);
  }

  /** @param {boolean} open */
  #setMenu(open) {
    const nav = this.querySelector('.nav');
    const toggle = this.querySelector('.nav-menu-toggle');
    const links = this.querySelector('.nav-links');
    if (!(nav instanceof HTMLElement) || !(toggle instanceof HTMLButtonElement)) return;
    nav.classList.toggle('open', open);
    toggle.setAttribute('aria-expanded', String(open));
    toggle.setAttribute('aria-label', (open ? toggle.dataset.closeLabel : toggle.dataset.openLabel) ?? toggle.getAttribute('aria-label') ?? '');
    // display:none 的移动端菜单由 CSS 管理；桌面导航始终可访问。
    if (links instanceof HTMLElement) links.removeAttribute('inert');
  }

  /** @param {Event} event */
  #click = (event) => {
    if (!(event.target instanceof Element)) return;
    const toggle = event.target.closest('.nav-menu-toggle');
    if (toggle instanceof HTMLButtonElement) {
      this.#setMenu(toggle.getAttribute('aria-expanded') !== 'true');
      return;
    }
    const anchor = event.target.closest('a[href^="#"]');
    if (anchor instanceof HTMLAnchorElement) {
      const target = anchor.hash.length > 1 ? this.querySelector(`#${CSS.escape(anchor.hash.slice(1))}`) : null;
      this.#setMenu(false);
      if (target instanceof HTMLElement) {
        // 原生锚点负责滚动和历史记录，焦点同步到对应内容。
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      }
      return;
    }
    const step = event.target.closest('.workflow-steps .step');
    if (step instanceof HTMLElement) this.#setWorkflow(step);
    const preview = event.target.closest('.hero-preview-tabs button');
    if (preview instanceof HTMLButtonElement) this.#setPreview(preview);
    const dot = event.target.closest('.hero-camera-dots button');
    if (dot instanceof HTMLButtonElement) this.#setSlide(dot);
    const kit = event.target.closest('.hero-kit-views button');
    if (kit instanceof HTMLButtonElement) {
      const visual = this.querySelector('.product-hero-visual');
      if (visual instanceof HTMLElement) visual.dataset.kitView = kit.dataset.kitView;
      this.#setPressed('.hero-kit-views button', kit);
    }
  };

  /** @param {KeyboardEvent} event */
  #keyDown = (event) => {
    if (event.key === 'Escape' && this.querySelector('.nav.open')) {
      this.#setMenu(false);
      const toggle = this.querySelector('.nav-menu-toggle');
      if (toggle instanceof HTMLElement) toggle.focus();
    }
    if (!(event.target instanceof Element)) return;
    const step = event.target.closest('.workflow-steps .step');
    if (step instanceof HTMLElement && !(step instanceof HTMLButtonElement) && ['Enter', ' '].includes(event.key)) {
      event.preventDefault();
      this.#setWorkflow(step);
    }
  };

  /** @param {FocusEvent} event */
  #focusIn = (event) => {
    if (!(event.target instanceof Element)) return;
    const step = event.target.closest('.workflow-steps .step');
    if (step instanceof HTMLElement) this.#setWorkflow(step);
    if (!event.target.closest('.nav')) this.#setMenu(false);
  };

  /** @param {PointerEvent} event */
  #pointerOver = (event) => {
    if (event.pointerType !== 'mouse' || !(event.target instanceof Element)) return;
    const step = event.target.closest('.workflow-steps .step');
    if (step instanceof HTMLElement && !(event.relatedTarget instanceof Node && step.contains(event.relatedTarget))) {
      this.#setWorkflow(step);
    }
  };

  /** @param {Event} event */
  #selectBlock = (event) => {
    if (!(event.target instanceof Element)) return;
    const step = event.target.closest('.workflow-steps .step');
    if (step instanceof HTMLElement) this.#setWorkflow(step);
  };

  /** @param {string} selector @param {HTMLElement} selected */
  #setPressed(selector, selected) {
    for (const item of this.querySelectorAll(selector)) {
      item.classList.toggle('active', item === selected);
      item.setAttribute('aria-pressed', String(item === selected));
    }
  }

  /** @param {HTMLElement} step */
  #setWorkflow(step) {
    this.#setPressed('.workflow-steps .step', step);
    const media = this.querySelector('[data-workflow-media]');
    if (media instanceof HTMLElement) media.dataset.visual = step.dataset.visual;
    for (const [selector, value] of [
      ['[data-workflow-step-label]', step.dataset.step],
      ['[data-workflow-title]', step.dataset.title],
      ['[data-workflow-text]', step.dataset.text],
      ['[data-workflow-note]', step.dataset.note],
    ]) {
      const target = this.querySelector(selector);
      if (target) target.textContent = value ?? '';
    }
  }

  /** @param {HTMLButtonElement} button */
  #setPreview(button) {
    const visual = this.querySelector('.product-hero-visual');
    if (visual instanceof HTMLElement) visual.dataset.preview = button.dataset.preview;
    this.#setPressed('.hero-preview-tabs button', button);
  }

  /** @param {HTMLButtonElement} button */
  #setSlide(button) {
    const index = Number(button.dataset.cameraSlide);
    const slides = this.querySelectorAll('.hero-camera-slide');
    if (!Number.isInteger(index) || index < 0 || index >= slides.length) return;
    slides.forEach((slide, slideIndex) => slide.classList.toggle('active', index === slideIndex));
    this.#setPressed('.hero-camera-dots button', button);
  }
}

if (!customElements.get('solutions-page')) customElements.define('solutions-page', SolutionsPage);
